import React from 'react';

import {useGate, useUnit} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath, useLocation} from 'react-router-dom';

import {ROUTES} from '@app/routing';
import {STATUS_COLOR_LIST} from '@consts/statuses';
import {downloadTaxpayersExcelFx, TaxpayersGate, taxpayersModel} from '@entities/taxpayers/model';
import {Taxpayer} from '@entities/taxpayers/types';
import {userModel} from '@entities/user/model';
import {GridFieldConfig} from '@shared/types/grid';
import {Icon} from '@shared/ui/Icon';
import {Link} from '@shared/ui/Link';
import {NumberRange} from '@shared/ui/NumberRange';
import {StatusWrapper} from '@shared/ui/PageComponents';
import {Select} from '@shared/ui/Select';
import {mapTableColumns, Table} from '@shared/ui/Table2.0';
import {formatNumberWithFraction} from '@utils/number';

import {NameCell, TinCell} from '../../styled';
import {Control} from '../Control';

type TaxpayerRow = Record<string, any>;

const option = <T,>(label: string, value: T | null) => ({label, value});

const allOption = (t: TFunction) => option(t('terms.all'), null);

const config = (t: TFunction): GridFieldConfig<keyof TaxpayerRow>[] => [
    {id: 'tin', header: t('page_taxpayers.table.tin'), isSortable: true, minWidth: 180},
    {id: 'name', header: t('page_taxpayers.table.name'), isSortable: true, minWidth: 230},
    {
        id: 'liquidated',
        header: t('page_taxpayers.table.status'),
        isSortable: true,
        width: 160,
        filter: (
            <Select
                isClearable
                options={[
                    allOption(t),
                    option(t('terms.taxpayer.liquidated'), true),
                    option(t('terms.taxpayer.not_liquidated'), false),
                ]}
            />
        ),
    },
    {
        id: 'surColour',
        header: t('page_taxpayers.table.sur'),
        isSortable: true,
        width: 160,
        filter: (
            <Select
                isClearable
                options={[
                    allOption(t),
                    option(t('terms.declaration.sur.red'), 'RED'),
                    option(t('terms.declaration.sur.yellow'), 'YELLOW'),
                    option(t('terms.declaration.sur.green'), 'GREEN'),
                ]}
            />
        ),
    },
    {
        id: 'transitOrGap',
        header: t('page_taxpayers.table.type'),
        isSortable: true,
        minWidth: 220,
        filter: (
            <Select
                isClearable
                options={[
                    allOption(t),
                    option(t('terms.taxpayer.other'), 'OTHER'),
                    option(t('terms.taxpayer.gap_source'), 'GAP_SOURCE'),
                    option(t('terms.taxpayer.transit_company'), 'TRANSIT_COMPANY'),
                ]}
            />
        ),
    },
    {
        id: 'shareDeduction',
        header: t('page_taxpayers.table.share_deduction'),
        isSortable: true,
        minWidth: 170,
        filter: <NumberRange />,
    },
    {
        id: 'isDecFiled',
        header: t('page_taxpayers.table.dec_status'),
        isSortable: true,
        minWidth: 150,
        filter: (
            <Select
                isClearable
                options={[
                    allOption(t),
                    option(t('terms.declaration.status.filled'), true),
                    option(t('terms.declaration.status.not_filled'), false),
                ]}
            />
        ),
    },
    {
        id: 'decType',
        header: t('page_taxpayers.table.dec_type'),
        isSortable: true,
        minWidth: 180,
        filter: (
            <Select
                isClearable
                options={[
                    allOption(t),
                    option(t('terms.declaration.type.not_filled'), 'NOT_FILED'),
                    option(t('terms.declaration.type.payable'), 'PAYABLE'),
                    option(t('terms.declaration.type.deduction'), 'DEDUCTION'),
                    option(t('terms.declaration.type.zero'), 'ZERO'),
                ]}
            />
        ),
    },
    {
        id: 'gapType',
        header: t('page_taxpayers.table.gap_type'),
        isSortable: true,
        minWidth: 180,
        filter: (
            <Select
                isClearable
                options={[
                    allOption(t),
                    option(t('terms.declaration.gap_type.dec_not_filled'), 'DECLARATION_NOT_FILED'),
                    option(t('terms.declaration.gap_type.tax_not_paid'), 'NDS_NOT_PAID'),
                    option(t('terms.declaration.gap_type.invoice_differ'), 'INVOICE_DIFFER'),
                    option(t('terms.declaration.gap_type.no_gap'), 'NO_GAP'),
                ]}
            />
        ),
    },
    {
        id: 'gapSum',
        header: t('page_taxpayers.table.gap_sum'),
        isSortable: true,
        minWidth: 170,
        filter: <NumberRange />,
    },
    {
        id: 'sumPayNdsDeclaration',
        header: t('page_taxpayers.table.sum_pay_nds_dec'),
        isSortable: true,
        minWidth: 240,
        filter: <NumberRange />,
    },
    {
        id: 'sumNdsDeductionDeclaration',
        header: t('page_taxpayers.table.sum_nds_deduction_dec'),
        isSortable: true,
        minWidth: 240,
        filter: <NumberRange />,
    },
    {
        id: 'saldoDeclaration',
        header: t('page_taxpayers.table.balance_dec'),
        isSortable: true,
        minWidth: 180,
        filter: <NumberRange />,
    },
    {
        id: 'sumPayNdsInvoice',
        header: t('page_taxpayers.table.sum_pay_nds_inv'),
        isSortable: true,
        minWidth: 240,
        filter: <NumberRange />,
    },
    {
        id: 'sumNdsDeductionInvoice',
        header: t('page_taxpayers.table.sum_nds_deduction_inv'),
        isSortable: true,
        minWidth: 240,
        filter: <NumberRange />,
    },
    {
        id: 'saldoInvoice',
        header: t('page_taxpayers.table.balance_inv'),
        isSortable: true,
        minWidth: 180,
        filter: <NumberRange />,
    },
];

const translateMap = {
    surColour: {
        YELLOW: 'terms.declaration.sur.yellow',
        GREEN: 'terms.declaration.sur.green',
        RED: 'terms.declaration.sur.red',
    },
    transitOrGap: {
        TRANSIT_COMPANY: 'terms.taxpayer.transit_company',
        GAP_SOURCE: 'terms.taxpayer.gap_source',
        OTHER: 'terms.taxpayer.other',
    },
    decType: {
        NOT_FILED: 'terms.declaration.type.not_filled',
        PAYABLE: 'terms.declaration.type.payable',
        DEDUCTION: 'terms.declaration.type.deduction',
        ZERO: 'terms.declaration.type.zero',
    },
    gapType: {
        DECLARATION_NOT_FILED: 'terms.declaration.gap_type.dec_not_filled',
        NDS_NOT_PAID: 'terms.declaration.gap_type.tax_not_paid',
        INVOICE_DIFFER: 'terms.declaration.gap_type.invoice_differ',
        NO_GAP: 'terms.declaration.gap_type.no_gap',
    },
};

const formatNullableNumber = (value: string | null) =>
    value == null ? '—' : formatNumberWithFraction(value);

const TAXPAYER_RISK_COLORS = {
    RED: STATUS_COLOR_LIST.RED,
    YELLOW: STATUS_COLOR_LIST.YELLOW,
    GREEN: STATUS_COLOR_LIST.GREEN,
} as const;

const TAXPAYER_STATUS_COLORS = {
    active: STATUS_COLOR_LIST.GREEN,
    inactive: STATUS_COLOR_LIST.GRAY_ALT,
    empty: STATUS_COLOR_LIST.GRAY,
} as const;

const TAXPAYER_GAP_TYPE_COLORS = {
    DECLARATION_NOT_FILED: STATUS_COLOR_LIST.ORANGE,
    NDS_NOT_PAID: STATUS_COLOR_LIST.RED,
    INVOICE_DIFFER: STATUS_COLOR_LIST.YELLOW,
    NO_GAP: STATUS_COLOR_LIST.GREEN,
} as const;

const dataMapper = ({
    raw,
    t,
    canOpenRelations,
    fromPath,
}: {
    raw?: Taxpayer[];
    t: TFunction;
    canOpenRelations: boolean;
    fromPath: string;
}): TaxpayerRow[] => {
    if (!raw?.length) return [];

    return raw.map((row) => ({
        ...row,
        tin: (
            <TinCell>
                <span>{row.tin}</span>
            </TinCell>
        ),
        name: (
            <NameCell>
                <Link
                    to={{
                        pathname: generatePath(ROUTES.TAXPAYER_DETAILS, {tin: row.tin}),
                        state: {from: fromPath},
                    }}
                >
                    {row.name}
                </Link>
                {canOpenRelations && (
                    <Link
                        to={{
                            pathname: ROUTES.RELATIONS,
                            search: `?tin=${row.tin}`,
                            state: {from: fromPath},
                        }}
                    >
                        <Icon name="balanceTab" width={16} />
                    </Link>
                )}
            </NameCell>
        ),
        liquidated: (
            <StatusWrapper
                color={
                    row.liquidated == null
                        ? TAXPAYER_STATUS_COLORS.empty
                        : row.liquidated
                        ? TAXPAYER_STATUS_COLORS.inactive
                        : TAXPAYER_STATUS_COLORS.active
                }
            >
                {row.liquidated == null
                    ? t('no_data')
                    : t(
                          row.liquidated
                              ? 'terms.taxpayer.liquidated'
                              : 'terms.taxpayer.not_liquidated',
                      )}
            </StatusWrapper>
        ),
        surColour: row.surColour ? (
            <StatusWrapper color={TAXPAYER_RISK_COLORS[row.surColour]}>
                {t(translateMap.surColour[row.surColour])}
            </StatusWrapper>
        ) : (
            <StatusWrapper color={STATUS_COLOR_LIST.GRAY_ALT}>{t('no_data')}</StatusWrapper>
        ),
        transitOrGap: row.transitOrGap ? t(translateMap.transitOrGap[row.transitOrGap]) : '—',
        shareDeduction: row.shareDeduction ? `${Math.round(Number(row.shareDeduction))}%` : '—',
        isDecFiled: (
            <StatusWrapper
                color={
                    row.isDecFiled
                        ? STATUS_COLOR_LIST.BLUE
                        : row.isDecFiled === false
                        ? STATUS_COLOR_LIST.RED
                        : STATUS_COLOR_LIST.GRAY_ALT
                }
            >
                {row.isDecFiled == null
                    ? t('no_data')
                    : t(
                          row.isDecFiled
                              ? 'terms.declaration.status.filled'
                              : 'terms.declaration.status.not_filled',
                      )}
            </StatusWrapper>
        ),
        decType: row.decType ? t(translateMap.decType[row.decType]) : '—',
        gapType: (
            <StatusWrapper
                color={
                    row.gapType
                        ? TAXPAYER_GAP_TYPE_COLORS[row.gapType]
                        : TAXPAYER_GAP_TYPE_COLORS.NO_GAP
                }
            >
                {row.gapType
                    ? t(translateMap.gapType[row.gapType]) ?? '—'
                    : t('terms.declaration.gap_type.no_gap')}
            </StatusWrapper>
        ),
        gapSum: formatNullableNumber(row.gapSum),
        sumPayNdsDeclaration: formatNullableNumber(row.sumPayNdsDeclaration),
        sumNdsDeductionDeclaration: formatNullableNumber(row.sumNdsDeductionDeclaration),
        saldoDeclaration: formatNullableNumber(row.saldoDeclaration),
        sumPayNdsInvoice: formatNullableNumber(row.sumPayNdsInvoice),
        sumNdsDeductionInvoice: formatNullableNumber(row.sumNdsDeductionInvoice),
        saldoInvoice: formatNullableNumber(row.saldoInvoice),
    }));
};

function downloadXlsx(buffer: ArrayBuffer, filename: string) {
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
}

export const TaxpayersList = () => {
    const taxpayers = useUnit(taxpayersModel);
    const isManagement = useUnit(userModel.isManagement);
    const location = useLocation();
    const {t} = useTranslation();

    useGate(TaxpayersGate);

    React.useEffect(
        () =>
            downloadTaxpayersExcelFx.doneData.watch((buffer: ArrayBuffer) => {
                downloadXlsx(buffer, `${t('page_taxpayers.filename')}.xlsx`);
            }),
        [t],
    );

    const data = React.useMemo(
        () =>
            dataMapper({
                raw: taxpayers.state.data?.taxpayers,
                t,
                canOpenRelations: isManagement,
                fromPath: location.pathname,
            }),
        [t, taxpayers.state.data, isManagement, location.pathname],
    );
    const columns = React.useMemo(
        () =>
            mapTableColumns(
                config(t),
                taxpayers.state.filters,
                taxpayers.filtersChanged,
                taxpayers.filtersReset,
                taxpayers.state.sortings,
                taxpayers.sortingsChanged,
            ),
        [t, taxpayers.state.filters, taxpayers.state.sortings],
    );

    return (
        <Table
            headerControls={<Control />}
            columns={columns}
            data={data}
            isLoading={taxpayers.state.pending}
            pageCount={taxpayers.state.pageCount}
            pageIndex={taxpayers.state.pageNumber}
            totalRowsCount={taxpayers.state.totalItems}
            onPageChange={taxpayers.pageNumberChanged}
            onPageSizeChange={taxpayers.pageSizeChanged}
            // exportConfig={{
            //     exportPending: taxpayers.state.exportPending,
            //     callback: taxpayers.downloadExcel,
            // }}
        />
    );
};
